import { computed, ComputedRef } from "vue";
import { storeToRefs } from "pinia";
import { useTasksStore } from "@/stores/v2";
import { Tank } from "@/types/v2";
import { isPreChallengeTask } from "@/stores/v2/tasks/utils";

export interface ChallengeMaterial {
  key: string;
  name: string;
  concentration: number;
  unit: string;
}

export interface SourceDilution {
  key: string;
  dilutionNumber: number;
  dilutionRatio: string;
}

export interface Dilution {
  key: string;
  dilutionNumber: number;
  dilutionRatio: string;
  challengeMaterial: ChallengeMaterial;
  sourceType: 'challenge_material' | 'dilution';
  sourceDilution: SourceDilution | null;
  volumeAdded: number;
  diluentVolume: number;
  diluentType: string;
  resultingConcentration: number;
  totalVolume: number;
  instructions: string;
  completed?: boolean;
  photos?: string[];
}

export interface DependentChallengeTask {
  key: string;
  title: string;
  status: string;
}

export function usePreChallenge() {
  // ===================================================================
  // Store
  // ===================================================================
  const { activeTask } = storeToRefs(useTasksStore());

  // ===================================================================
  // State - Direct references to activeTask data
  // ===================================================================
  const challengeMaterials: ComputedRef<ChallengeMaterial[]> = computed(() => {
    if (!activeTask.value || !isPreChallengeTask(activeTask.value)) return [];
    return activeTask.value.details.challengeMaterials || [];
  });

  const dilutions: ComputedRef<Dilution[]> = computed(() => {
    if (!activeTask.value || !isPreChallengeTask(activeTask.value)) return [];
    return activeTask.value.details.dilutions || [];
  });

  const tanks: ComputedRef<Tank[]> = computed(() => {
    if (!activeTask.value || !isPreChallengeTask(activeTask.value)) return [];
    return activeTask.value.tanks || [];
  });

  const dependentChallengeTask: ComputedRef<DependentChallengeTask | null> = computed(() => {
    if (!activeTask.value || !isPreChallengeTask(activeTask.value)) return null;
    return activeTask.value.details.dependentChallengeTask || null;
  });

  // ===================================================================
  // Computed
  // ===================================================================
  const completedDilutions = computed(() => {
    return dilutions.value.filter((dilution: Dilution) => dilution.completed);
  });

  const incompleteDilutions = computed(() => {
    return dilutions.value.filter((dilution: Dilution) => !dilution.completed);
  });

  const completionProgress = computed(() => {
    if (dilutions.value.length === 0) return 0;
    return (completedDilutions.value.length / dilutions.value.length) * 100;
  });

  const isAllCompleted = computed(() => {
    return dilutions.value.length > 0 && incompleteDilutions.value.length === 0;
  });

  // ===================================================================
  // Methods - Work directly with activeTask data
  // ===================================================================
  const markDilutionComplete = (dilutionKey: string, photos: string[] = []) => {
    if (!activeTask.value || !isPreChallengeTask(activeTask.value)) return;
    
    const dilution = activeTask.value.details.dilutions?.find((d: any) => d.key === dilutionKey);
    if (dilution) {
      dilution.completed = true;
      // Preserve existing photos and add new ones
      if (!dilution.photos) dilution.photos = [];
      if (photos.length > 0) {
        dilution.photos.push(...photos);
      }
    }
  };

  const markDilutionIncomplete = (dilutionKey: string) => {
    if (!activeTask.value || !isPreChallengeTask(activeTask.value)) return;
    
    const dilution = activeTask.value.details.dilutions?.find((d: any) => d.key === dilutionKey);
    if (dilution) {
      dilution.completed = false;
      // Keep photos when marking incomplete - user might want to re-complete
    }
  };

  const uploadPhoto = async (dilutionKey: string, file: File): Promise<string> => {
    try {
      // Create a local URL for the file to show preview immediately
      const localUrl = URL.createObjectURL(file);

      // Find the dilution in activeTask and add the photo directly
      if (activeTask.value && isPreChallengeTask(activeTask.value)) {
        const dilution = activeTask.value.details.dilutions?.find((d: any) => d.key === dilutionKey);
        if (dilution) {
          if (!dilution.photos) dilution.photos = [];
          dilution.photos.push(localUrl);
        }
      }

      // TODO: In real implementation, upload to your backend here
      // const uploadedUrl = await actualUploadToBackend(file);
      // Replace localUrl with uploadedUrl in the dilution.photos array

      return localUrl;
    } catch (error) {
      console.error('Error processing photo:', error);
      throw error;
    }
  };

  const addPhoto = (dilutionKey: string, photoData: string) => {
    if (!activeTask.value || !isPreChallengeTask(activeTask.value)) return;
    
    const dilution = activeTask.value.details.dilutions?.find((d: any) => d.key === dilutionKey);
    if (dilution) {
      if (!dilution.photos) dilution.photos = [];
      dilution.photos.push(photoData);
    }
  };

  const removePhoto = (dilutionKey: string, photoIndex: number) => {
    if (!activeTask.value || !isPreChallengeTask(activeTask.value)) return;
    
    const dilution = activeTask.value.details.dilutions?.find((d: any) => d.key === dilutionKey);
    if (dilution && dilution.photos) {
      dilution.photos.splice(photoIndex, 1);
    }
  };

  const getDilutionsByMaterial = (materialKey: string) => {
    return dilutions.value.filter((dilution: Dilution) =>
      dilution.challengeMaterial.key === materialKey
    );
  };

  const getSourceDilutionName = (dilution: Dilution): string => {
    if (dilution.sourceType === 'challenge_material') {
      return dilution.challengeMaterial.name;
    } else if (dilution.sourceDilution) {
      return `Dilution ${dilution.sourceDilution.dilutionNumber}`;
    }
    return 'Unknown source';
  };

  return {
    // ===================================================================
    // State
    // ===================================================================
    challengeMaterials,
    dilutions,
    tanks,
    dependentChallengeTask,

    // ===================================================================
    // Computed
    // ===================================================================
    completedDilutions,
    incompleteDilutions,
    completionProgress,
    isAllCompleted,

    // ===================================================================
    // Methods
    // ===================================================================
    markDilutionComplete,
    markDilutionIncomplete,
    uploadPhoto,
    addPhoto,
    removePhoto,
    getDilutionsByMaterial,
    getSourceDilutionName,
  };
}